import { useEffect, useRef, type ReactNode } from 'react'
import { ChevronRight } from 'lucide-react'

/** One folded group of invoice details: a title and a one-line summary that open to the full section. */
export function DetailSection({
  title,
  summary,
  open,
  children,
}: {
  title: string
  summary?: ReactNode
  /** Opens the section, e.g. when a link points into it. */
  open?: boolean
  children: ReactNode
}) {
  const ref = useRef<HTMLDetailsElement>(null)
  useEffect(() => {
    if (open && ref.current) ref.current.open = true
  }, [open])
  return (
    <details ref={ref} aria-label={title} className="group">
      <summary className="flex cursor-pointer list-none items-center gap-3 px-4 py-3 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
        <ChevronRight
          className="size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-90 motion-reduce:transition-none"
          aria-hidden="true"
        />
        <span className="text-sm font-semibold">{title}</span>
        {summary && (
          <span className="ml-auto min-w-0 truncate text-right text-sm text-muted-foreground">
            {summary}
          </span>
        )}
      </summary>
      <div className="px-4 pb-4 pt-1">{children}</div>
    </details>
  )
}

/** The folded sections share one panel, divided by hairlines. */
export function DetailSections({ children }: { children: ReactNode }) {
  return <div className="divide-y overflow-hidden rounded-xl border bg-card">{children}</div>
}
